import React from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Layout,
  Table,
  Button,
  Tag,
  Row,
  Col,
  Modal,
  Dropdown,
  message,
  Drawer,
  Descriptions,
  Space,
  Select,
} from "antd";
import Search from "antd/es/input/Search";
import {
  DeleteOutlined,
  CopyOutlined,
  MoreOutlined,
  DownloadOutlined,
  MailOutlined,
  UserOutlined,
  BellOutlined,
  FilePdfOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import QRCode from "react-qr-code";
import { Header } from "components";
import { CardInfo } from "components/card/card-info";
import { RewardVoucherQrCodePrint } from "components/reward/RewardVoucherQrCodePrint";
import { getRewardBatch, cancelRewardBatch } from "services/reward-batch-service";
import { cancelRewardVouchers, resendRewardVoucher, resendVoucherNotification } from "services/reward-voucher-service";
import { formatMoney, formatDate, exportXls, getMaskedCpf } from "utils";
import { MONTHLY_REVENUE_RANGE_DATA } from "utils/static-data";

const { Content, Footer } = Layout;

const mapStateToProps = (state) => ({
  login: state.authReducer.loginReducer,
});

const RewardBatchDetailScreen = (props) => {
  const { login } = props;
  const { batchId, campaignId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = React.useState(null);
  const [vouchers, setVouchers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [qrDataUrls, setQrDataUrls] = React.useState({});
  const [rescuerModal, setRescuerModal] = React.useState({ show: false, data: null });
  const [qrModal, setQrModal] = React.useState({ show: false, voucher: null });
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [voucherStatusFilter, setVoucherStatusFilter] = React.useState(null);
  const [voucherSearch, setVoucherSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const qrPrintRef = React.useRef();

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(voucherSearch), 300);
    return () => clearTimeout(timer);
  }, [voucherSearch]);

  React.useEffect(() => {
    if (!batchId) return;
    setPage(0);
    loadBatch(0, pageSize);
  }, [batchId, voucherStatusFilter, debouncedSearch, pageSize]);

  React.useEffect(() => {
    if (batchId && page > 0) loadBatch(page, pageSize);
  }, [page]);

  const loadBatch = async (p = page, s = pageSize) => {
    setLoading(true);
    try {
      const res = await getRewardBatch(batchId, p, s, voucherStatusFilter, debouncedSearch || null);
      if (res.success) {
        setBatch(res.batch);
        setVouchers(res.vouchers || []);
        setTotal(res.total || 0);
      }
    } catch (e) {
      message.error("Erro ao carregar lote");
    } finally {
      setLoading(false);
    }
  };

  const onCancelBatch = () => {
    Modal.confirm({
      title: "Cancelar Lote",
      content:
        "Tem certeza que deseja cancelar este lote? Os valores serão retornados ao saldo da empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await cancelRewardBatch(batchId);
          message.success("Lote cancelado com sucesso.");
          await loadBatch();
        } catch (e) {
          message.error("Erro ao cancelar lote.");
        }
      },
    });
  };

  const onCancelVoucher = (id) => {
    Modal.confirm({
      title: "Cancelar Voucher",
      content:
        "Tem certeza que deseja cancelar este voucher? O valor será retornado ao saldo da empresa.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await cancelRewardVouchers([id]);
          message.success("Voucher cancelado com sucesso.");
          await loadBatch();
        } catch (e) {
          message.error("Erro ao cancelar voucher.");
        }
      },
    });
  };

  const onCancelSelected = () => {
    Modal.confirm({
      title: `Cancelar ${selectedRowKeys.length} voucher(s)`,
      content:
        "Tem certeza? Os valores serão retornados ao saldo da empresa. Se todos os vouchers ativos forem cancelados, o lote também será cancelado.",
      okText: "Sim, cancelar",
      okButtonProps: { danger: true },
      cancelText: "Não",
      onOk: async () => {
        try {
          await cancelRewardVouchers(selectedRowKeys);
          const remainingActive = (batch?.activeVouchers ?? 0) - selectedRowKeys.length;
          if (remainingActive <= 0) {
            await cancelRewardBatch(batchId);
            message.success("Vouchers e lote cancelados com sucesso.");
          } else {
            message.success(`${selectedRowKeys.length} voucher(s) cancelado(s).`);
          }
          setSelectedRowKeys([]);
          await loadBatch();
        } catch (e) {
          message.error("Erro ao cancelar vouchers.");
        }
      },
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return "—";
    const d = phone.replace(/\D/g, "").replace(/^55/, "").slice(0, 11);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return phone;
  };

  const formatIncomeRange = (code) =>
    MONTHLY_REVENUE_RANGE_DATA.find((x) => x.code === code)?.name || code || "—";

  const formatGender = (code) =>
    code === "M" ? "Masculino" : code === "F" ? "Feminino" : "Prefiro não informar";

  const onCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    message.success("Link copiado!");
  };

  const onExportExcel = async () => {
    setExportLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
    const rows = vouchers.map((v) => {
      const rescued = v.status?.code === "rescued";
      return {
        "E-mail": (rescued ? v.redeemerEmail : v.email) || v.email || "",
        Telefone: rescued
          ? (v.redeemerPhone ? formatPhone(v.redeemerPhone) : v.phone || "")
          : v.phone || "",
        Valor: formatMoney(v.amount, false),
        "Criado em": v.createdAt ? formatDate(v.createdAt) : "",
        "Expira em": v.expirationDate ? formatDate(v.expirationDate) : "",
        Link: v.link || "",
        Status: v.status?.name || "",
        Nome: v.redeemerName || "",
        CPF: v.redeemerCpf ? getMaskedCpf(v.redeemerCpf) : "",
        "E-mail Resgatador": v.redeemerEmail || "",
        "Telefone Resgatador": v.redeemerPhone ? formatPhone(v.redeemerPhone) : "",
        Sexo: rescued ? formatGender(v.redeemerGender) : "",
        "Data de Nascimento": rescued && v.redeemerBirthdate ? formatDate(v.redeemerBirthdate) : "",
        Ocupação: v.redeemerOccupation || "",
        "Faixa Salarial": formatIncomeRange(v.redeemerIncomeRange),
        CEP: v.redeemerAddressZip || "",
        Rua: v.redeemerAddressStreet || "",
        Número: v.redeemerAddressNumber || "",
        Complemento: v.redeemerAddressComplement || "",
        Bairro: v.redeemerAddressNeighborhood || "",
        Cidade: v.redeemerAddressCity || "",
        UF: v.redeemerAddressState || "",
        "Data do Resgate": v.rescuedAt ? formatDate(v.rescuedAt) : "",
        ...(batch?.redemptionType?.code === "PIX" ? {
          "Banco Receptor": v.pixBankName || "",
          "Titular da Chave": v.pixHolderName || "",
          "Doc. do Titular": v.pixHolderDocument ? getMaskedCpf(v.pixHolderDocument) : "",
          "Chave PIX": v.pixKey || "",
          "Tipo da Chave": v.pixKeyType || "",
          "PIX Iniciado em": v.pixInitiatedAt ? formatDate(v.pixInitiatedAt, "DD/MM/YYYY HH:mm") : "",
          "PIX Confirmado em": v.pixConfirmedAt ? formatDate(v.pixConfirmedAt, "DD/MM/YYYY HH:mm") : "",
        } : {}),
      };
    });
    exportXls().createAndSaveFile(
      ["Vouchers"],
      { Vouchers: rows },
      `lote_${batch?.name || batchId}`
    );
    } finally {
      setExportLoading(false);
    }
  };

  const statusTag = (status) => {
    const colorMap = {
      actived: "green",
      rescued: "purple",
      cancelled: "red",
      pix_sent: "blue",
    };
    const color = colorMap[status?.code] ?? "default";
    return <Tag color={color}>{status?.name || "-"}</Tag>;
  };

  const totalActived = batch?.totalActivedAmount ?? 0;
  const totalRescued = batch?.totalRescuedAmount ?? 0;

  const columns = [
    {
      title: "E-mail",
      dataIndex: "email",
      key: "email",
      align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Telefone",
      dataIndex: "phone",
      key: "phone",
      align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      width: 120,
      render: (v) => formatMoney(v),
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      width: 140,
      render: (v) => formatDate(v, "DD/MM/YYYY HH:mm"),
    },
    {
      title: "Resgatado em",
      dataIndex: "rescuedAt",
      key: "rescuedAt",
      align: "center",
      width: 140,
      render: (v, record) =>
        record.status?.code === "rescued" && v
          ? formatDate(v, "DD/MM/YYYY HH:mm")
          : "—",
    },
    {
      title: "Expira em",
      dataIndex: "expirationDate",
      key: "expirationDate",
      align: "center",
      width: 110,
      render: (v) => formatDate(v),
    },
    {
      title: "Link",
      key: "link",
      align: "center",
      render: (_, record) => {
        const canCopy = record.status?.code === "actived";
        return canCopy ? (
          <Button
            size="small"
            type="link"
            icon={<CopyOutlined />}
            onClick={() => onCopyLink(record.link)}
          >
            Copiar
          </Button>
        ) : (
          "—"
        );
      },
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 110,
      render: (_, record) => statusTag(record.status),
    },
    {
      title: "Ações",
      key: "actions",
      align: "center",
      width: 80,
      fixed: "right",
      render: (_, record) => {
        const items = [];
        if (record.status?.code === "actived") {
          items.push({
            key: "qrcode",
            icon: <QrcodeOutlined />,
            label: "Ver QR Code",
            onClick: () => setQrModal({ show: true, voucher: record }),
          });
        }
        if (record.status?.code === "rescued" || record.status?.code === "pix_sent") {
          items.push({
            key: "rescuer",
            icon: <UserOutlined />,
            label: "Ver Resgatador",
            onClick: () => setRescuerModal({ show: true, data: record }),
          });
        }
        if (record.status?.code === "rescued") {
          if (items.length > 0) items.push({ type: "divider" });
          items.push({
            key: "resend-receipt",
            icon: <MailOutlined />,
            label: "Reenviar Comprovante",
            onClick: () => resendRewardVoucher(record.id),
          });
        }
        if (record.status?.code === "actived" && (record.email || record.phone)) {
          if (items.length > 0) items.push({ type: "divider" });
          items.push({
            key: "resend-notification",
            icon: <BellOutlined />,
            label: "Reenviar Notificação",
            onClick: () => resendVoucherNotification(record.id),
          });
        }
        if (record.status?.code === "actived" && !login.company.master) {
          if (items.length > 0) items.push({ type: "divider" });
          items.push({
            key: "cancel",
            icon: <DeleteOutlined />,
            label: "Cancelar",
            danger: true,
            onClick: () => onCancelVoucher(record.id),
          });
        }
        if (items.length === 0) return "—";
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button size="small" type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.status?.code !== "actived",
    }),
  };

  const downloadPdfQrCodes = async () => {
    setPdfLoading(true);
    try {
      const selectedVouchers = vouchers.filter((v) => selectedRowKeys.includes(v.id));
      const QRCodeLib = (await import("qrcode")).default;
      const urls = {};
      await Promise.all(
        selectedVouchers.map(async (v) => {
          urls[v.id] = await QRCodeLib.toDataURL(v.link, { margin: 1, width: 250 });
        })
      );
      setQrDataUrls(urls);
      await new Promise((r) => setTimeout(r, 300));

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const element = qrPrintRef.current;
      const pageEls = element.querySelectorAll("[data-pdf-page]");
      const pdf = new jsPDF("p", "mm", "a4");

      for (let i = 0; i < pageEls.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(pageEls[i], { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 210, 297);
      }

      pdf.save(`qrcodes_${batch?.name || batchId}.pdf`);
    } catch {
      message.error("Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const isActive = batch?.status?.code === "active";

  return (
    <Layout className="alymente-layout">
      <Header title={batch ? `Lote: ${batch.name}` : "Link+"} />
      <Content style={{ padding: 16 }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <CardInfo
              style={{ minWidth: 220, minHeight: 115 }}
              loading={loading}
              title="Valor Total"
              value={formatMoney(batch?.totalAmount || 0)}
            />
          </Col>
          <Col>
            <CardInfo
              style={{ minWidth: 220, minHeight: 115 }}
              loading={loading}
              title="Ativos"
              value={formatMoney(totalActived)}
            />
          </Col>
          <Col>
            <CardInfo
              style={{ minWidth: 220, minHeight: 115 }}
              loading={loading}
              title="Resgatados"
              value={formatMoney(totalRescued)}
            />
          </Col>
        </Row>

        <Row gutter={[12, 8]} style={{ marginBottom: 12 }} align="middle">
          <Col>
            <Select
              allowClear
              placeholder="Filtrar por status"
              style={{ width: 180 }}
              onChange={setVoucherStatusFilter}
              options={[
                { value: "actived", label: "Ativo" },
                { value: "rescued", label: "Resgatado" },
                { value: "cancelled", label: "Cancelado" },
                { value: "pix_sent", label: "PIX Enviado" },
              ]}
            />
          </Col>
          <Col>
            <Search
              placeholder="Buscar por e-mail ou telefone"
              allowClear
              style={{ width: 280 }}
              value={voucherSearch}
              onChange={(e) => setVoucherSearch(e.target.value)}
              onSearch={(v) => setVoucherSearch(v)}
            />
          </Col>
        </Row>

        <Table
          dataSource={vouchers}
          columns={columns}
          loading={loading}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: page + 1,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            onChange: (p, s) => { setPageSize(s); setPage(s !== pageSize ? 0 : p - 1); },
          }}
          bordered
          size="small"
          scroll={{ x: true }}
        />

      </Content>

      <Footer style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
        <Space>
          <Button type="primary" onClick={() => navigate(campaignId ? `/reward-voucher/${campaignId}` : "/reward-voucher")}>
            Voltar
          </Button>
        </Space>
        <Space>
          <Button icon={<DownloadOutlined />} onClick={onExportExcel} loading={exportLoading} disabled={vouchers.length === 0}>
            Exportar Relatório
          </Button>
          {selectedRowKeys.length > 0 && (
            <Button icon={<FilePdfOutlined />} onClick={downloadPdfQrCodes} loading={pdfLoading}>
              Gerar QR Codes ({selectedRowKeys.length})
            </Button>
          )}
          {selectedRowKeys.length > 0 && !login.company.master && (
            <Button danger icon={<DeleteOutlined />} onClick={onCancelSelected}>
              Cancelar Selecionados ({selectedRowKeys.length})
            </Button>
          )}
        </Space>
      </Footer>

      <Modal
        title={null}
        open={qrModal.show}
        onCancel={() => setQrModal({ show: false, voucher: null })}
        footer={null}
        centered
        width={360}
        styles={{ body: { padding: "32px 0 0" } }}
      >
        {qrModal.voucher && (
          <div style={{ borderRadius: 12, overflow: "hidden", fontFamily: "'Montserrat', sans-serif", backgroundColor: "#1b2f4e" }}>
            <div style={{ padding: "24px 20px 20px", textAlign: "center", color: "#fff", display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 26, fontWeight: "bold" }}>Parabéns!</span>
              <span style={{ fontSize: 13, opacity: 0.9 }}>Você ganhou</span>
              <span style={{ fontSize: 32, fontWeight: "bold", fontStyle: "italic" }}>
                {formatMoney(qrModal.voucher.amount)}
              </span>
              <span style={{ fontSize: 13, opacity: 0.9 }}>
                da campanha {batch?.name}
              </span>
            </div>
            <div style={{ backgroundColor: "#fff", borderRadius: "14px 14px 0 0", display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 20px 28px", gap: 14 }}>
              <QRCode value={qrModal.voucher.link} size={200} />
              <p style={{ margin: 0, fontSize: 13, color: "#394D6D", textAlign: "center", fontWeight: 600, lineHeight: 1.5 }}>
                Escaneie o QR Code<br />e resgate seu prêmio
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#666" }}>
                Válido até: {formatDate(qrModal.voucher.expirationDate)} 📅
              </p>
            </div>
          </div>
        )}
      </Modal>

      <div style={{ position: "absolute", left: "-9999px", top: 0, opacity: 0, pointerEvents: "none" }}>
        <RewardVoucherQrCodePrint
          ref={qrPrintRef}
          vouchers={vouchers.filter((v) => selectedRowKeys.includes(v.id))}
          batchName={batch?.name || ""}
          qrDataUrls={qrDataUrls}
        />
      </div>

      <Drawer
        title="Dados do Resgatador"
        open={rescuerModal.show}
        onClose={() => setRescuerModal({ show: false, data: null })}
        width={480}
      >
        {rescuerModal.data ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Nome">{rescuerModal.data.redeemerName || "—"}</Descriptions.Item>
            <Descriptions.Item label="CPF">{rescuerModal.data.redeemerCpf ? getMaskedCpf(rescuerModal.data.redeemerCpf) : "—"}</Descriptions.Item>
            <Descriptions.Item label="E-mail">{rescuerModal.data.redeemerEmail || "—"}</Descriptions.Item>
            <Descriptions.Item label="Telefone">{formatPhone(rescuerModal.data.redeemerPhone)}</Descriptions.Item>
            <Descriptions.Item label="Sexo">
              {formatGender(rescuerModal.data.redeemerGender)}
            </Descriptions.Item>
            <Descriptions.Item label="Data de Nascimento">
              {rescuerModal.data.redeemerBirthdate
                ? formatDate(rescuerModal.data.redeemerBirthdate)
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Ocupação">{rescuerModal.data.redeemerOccupation || "—"}</Descriptions.Item>
            <Descriptions.Item label="Faixa Salarial">
              {formatIncomeRange(rescuerModal.data.redeemerIncomeRange)}
            </Descriptions.Item>
            <Descriptions.Item label="CEP">{rescuerModal.data.redeemerAddressZip || "—"}</Descriptions.Item>
            <Descriptions.Item label="Rua">{rescuerModal.data.redeemerAddressStreet || "—"}</Descriptions.Item>
            <Descriptions.Item label="Número">{rescuerModal.data.redeemerAddressNumber || "—"}</Descriptions.Item>
            <Descriptions.Item label="Complemento">{rescuerModal.data.redeemerAddressComplement || "—"}</Descriptions.Item>
            <Descriptions.Item label="Bairro">{rescuerModal.data.redeemerAddressNeighborhood || "—"}</Descriptions.Item>
            <Descriptions.Item label="Cidade">{rescuerModal.data.redeemerAddressCity || "—"}</Descriptions.Item>
            <Descriptions.Item label="UF">{rescuerModal.data.redeemerAddressState || "—"}</Descriptions.Item>
            <Descriptions.Item label="Data do Resgate">
              {rescuerModal.data.rescuedAt ? formatDate(rescuerModal.data.rescuedAt) : "—"}
            </Descriptions.Item>
            {batch?.redemptionType?.code === "PIX" && (
              <>
                <Descriptions.Item label="Banco Receptor">{rescuerModal.data.pixBankName || "—"}</Descriptions.Item>
                <Descriptions.Item label="Titular da Chave">{rescuerModal.data.pixHolderName || "—"}</Descriptions.Item>
                <Descriptions.Item label="Doc. do Titular">
                  {rescuerModal.data.pixHolderDocument ? getMaskedCpf(rescuerModal.data.pixHolderDocument) : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Chave PIX">{rescuerModal.data.pixKey || "—"}</Descriptions.Item>
                <Descriptions.Item label="Tipo da Chave">{rescuerModal.data.pixKeyType || "—"}</Descriptions.Item>
                <Descriptions.Item label="PIX Iniciado em">
                  {rescuerModal.data.pixInitiatedAt ? formatDate(rescuerModal.data.pixInitiatedAt, "DD/MM/YYYY HH:mm") : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="PIX Confirmado em">
                  {rescuerModal.data.pixConfirmedAt ? formatDate(rescuerModal.data.pixConfirmedAt, "DD/MM/YYYY HH:mm") : "—"}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        ) : (
          <p>Sem dados disponíveis.</p>
        )}
      </Drawer>
    </Layout>
  );
};

export default connect(mapStateToProps, {})(RewardBatchDetailScreen);
